import { get } from "nodash"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { AccountRegistrationPayload } from "user/registration/types"

import { fetchApiGateway } from "@nova/fetch-api-gateway"
import type { ApiRegistry } from "@nova/contracts"

import { getBrowserFingerprint } from "tools/browser/fingerprint"
import { isNonEmptyString } from "tools/strings/isNonEmptyString"

export type RegistrationStartRequestData = ApiRegistry["post /auth/registration/start"]["inputBody"]
export type RegistrationStartResponse = ApiRegistry["post /auth/registration/start"]["outputType"]
export type RegistrationCompleteRequest =
    ApiRegistry["post /auth/registration/complete"]["inputBody"]
export type RegistrationCompleteResponse =
    ApiRegistry["post /auth/registration/complete"]["outputType"]

const normalizeCampaignTrackingMetrics = (
    utm?: AccountRegistrationPayload["campaignTrackingMetrics"]
): RegistrationStartRequestData["campaignTrackingMetrics"] => {
    if (!utm) return undefined

    return {
        utm_source: utm.utm_source ?? "",
        utm_medium: utm.utm_medium ?? "",
        utm_campaign: utm.utm_campaign ?? "",
        utm_content: utm.utm_content ?? "",
        utm_term: utm.utm_term ?? "",
        gclid: utm.gclid ?? "",
    }
}

export const registerAccount = createAsyncThunk<Promise<boolean>, AccountRegistrationPayload>(
    "registration/createAccount",
    async (values: AccountRegistrationPayload, thunkApi) => {
        const email = get(values, "email")
        if (!isNonEmptyString(email)) {
            thunkApi.rejectWithValue("Missing email value")

            return false
        }

        try {
            await fetchApiGateway("post /auth/registration/complete", {
                body: { email },
            })

            return true
        } catch (error) {
            console.error("Failed to complete registration:", error)
            thunkApi.rejectWithValue("Unable to complete registration")

            return false
        }
    }
)

export const requestRegistration = createAsyncThunk<
    RegistrationStartResponse,
    AccountRegistrationPayload,
    { rejectValue: string }
>("registration/requestRegistration", async (values, thunkApi) => {
    const email = get(values, "email")
    const campaignTrackingMetrics = get(values, "campaignTrackingMetrics")

    if (!isNonEmptyString(email)) {
        return thunkApi.rejectWithValue("Missing email value")
    }

    try {
        const bid = getBrowserFingerprint()

        const data = await fetchApiGateway("post /auth/registration/start", {
            body: {
                email,
                bid: `${bid}`,
                consentMarketing: true,
                campaignTrackingMetrics: normalizeCampaignTrackingMetrics(campaignTrackingMetrics),
            },
        })

        return data
    } catch (error) {
        console.error("Failed to initiate registration:", error)
        return thunkApi.rejectWithValue("Unable to initiate registration")
    }
})
